import { Container, FormControl, InputLabel, MenuItem, Select, Stack, Typography } from "@mui/material";

import React, { useEffect, useMemo } from "react";
import { useTranslation } from "react-i18next";

import { IntegrationEnvironment, IntegrationState } from "../ServerEndpoints";
import {
  ButtonContent,
  CertificateIcon,
  CopartnershipAlert,
  Header,
  LogoPz,
  Signature,
  SoleProprietorshipAlert,
  TokenIcon,
} from "../components";
import { config } from "../config";
import { useStore } from "../store";

export const SelectMethod = () => {
  const { t } = useTranslation();
  const { integration, integrationId, setIntegrationId, setEnvironment, environment, setIntegrationMethod } =
    useStore();

  const testParam = useMemo(() => {
    const urlParams = new URLSearchParams(window.location.search);
    return urlParams.get("test");
  }, []);

  // Test and Demo option is always available unless in production (which requires test=true parameter)
  const isTestAndDemoKsefAvailable = config.isProductionEnvironment ? testParam === "true" : true;

  const integrationStatus = integration?.state;

  const onSelectTrustedProfile = () => setIntegrationMethod("TrustedProfile");
  const onSelectSignature = () => setIntegrationMethod("Signature");
  const onSelectToken = () => setIntegrationMethod("Token");
  const onSelectCertificate = () => setIntegrationMethod("Certificate");

  useEffect(() => {
    if (integrationId) {
      setIntegrationId(integrationId);
    }
  }, [integrationId]); // eslint-disable-line react-hooks/exhaustive-deps

  const SelectEnvironment = () => (
    <FormControl fullWidth>
      <InputLabel id="demo-simple-select-label">{t("selectMethod.environment")}</InputLabel>
      <Select<IntegrationEnvironment>
        color="primary"
        size="small"
        fullWidth
        labelId="demo-simple-select-label"
        id="demo-simple-select"
        value={environment}
        label={t("selectMethod.environment")}
        onChange={(ev) => setEnvironment(ev.target.value as IntegrationEnvironment)}
      >
        <MenuItem value="Prod">{t("selectMethod.production")}</MenuItem>
        <MenuItem value="Demo">{t("selectMethod.demo")}</MenuItem>
        <MenuItem value="Test">{t("selectMethod.test")}</MenuItem>
      </Select>
    </FormControl>
  );

  return (
    <Container maxWidth="sm">
      <Stack spacing={4}>
        <Header
          titleLoc="selectMethod.title"
          action={
            config.isProductionEnvironment && !isTestAndDemoKsefAvailable ? (
              <Typography variant="body1" textAlign="center">
                {t("selectMethod.productionEnvironment")}
              </Typography>
            ) : (
              <SelectEnvironment />
            )
          }
        />

        <Stack spacing={0}>
          {integration?.name && (
            <Typography
              variant="body1"
              sx={{
                textOverflow: "ellipsis",
                textWrap: "nowrap",
                overflow: "hidden",
              }}
            >
              {integration?.name}
            </Typography>
          )}
          <Typography variant="body2">{t("common.taxNo", { taxNo: integration?.taxNo ?? "" })}</Typography>
        </Stack>
        <CopartnershipAlert />
        <SoleProprietorshipAlert />

        <Stack spacing={1}>
          <Typography variant="body2">{t("selectMethod.recommendedMethods")}</Typography>

          <ButtonContent
            onClick={onSelectTrustedProfile}
            title={t("trustedProfileMethod.shortTitle")}
            leftIcon={<LogoPz sx={{ fontSize: 36 }} />}
          />

          <ButtonContent
            onClick={onSelectSignature}
            title={t("signatureMethod.shortTitle")}
            leftIcon={<Signature sx={{ fontSize: 36 }} />}
          />
        </Stack>

        <Stack spacing="9px">
          <Typography variant="body2">{t("selectMethod.otherMethods")}</Typography>
          <Stack spacing={2}>
            <ButtonContent
              disabled={integrationStatus === IntegrationState.SessionSignedRequestGenerated}
              onClick={onSelectToken}
              title={t("tokenMethod.shortTitle")}
              leftIcon={<TokenIcon sx={{ fontSize: 36 }} />}
              tooltip={t("tokenMethod.description")}
            />
            <ButtonContent
              disabled={integrationStatus === IntegrationState.SessionSignedRequestGenerated}
              onClick={onSelectCertificate}
              title={t("certificateMethod.shortTitle")}
              leftIcon={<CertificateIcon sx={{ fontSize: 36 }} />}
              tooltip={t("certificateMethod.description")}
            />
          </Stack>
        </Stack>
      </Stack>
    </Container>
  );
};
